import { apiClient } from "./apiClient";
import { extractCommentaryArray } from "./utils/dataValidators";

/**
 * Obtiene los comentarios/narraciones del partido según el ID
 * @param {string} matchId - ID del partido
 * @param {string} country - País para filtrar datos (parámetro query)
 * @returns {Promise<Object>} Datos de comentarios del partido
 */
export const getCommentary = async (matchId, country = null) => {
  try {
    const response = await apiClient.get(
      `/api/core-clarosports/v2/events/match/${matchId}/commentary`,
      {
        params: {
          ...(country === 'us' && { country })
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching commentary data:", error.message);
    throw error;
  }
};

/**
 * Transforma los datos del API de comentarios a un formato compatible con los componentes
 * Mantiene el orden de recepción de los eventos
 * @param {Object} apiData - Datos crudos del API de comentarios
 * @returns {Array} Array de comentarios transformados (en orden de recepción)
 */
export const transformCommentaryData = (apiData) => {
  const commentArray = extractCommentaryArray(apiData);
  return commentArray.map((comment) => transformCommentItem(comment));
};

/**
 * Transforma un item de comentario individual
 * Estructura de datos esperada:
 * {
 *   "id": "2978996937",
 *   "comment": "Final del partido' Tigres 3, Toluca 2.",
 *   "timestamp": "2026-10-10T05:02:11Z",
 *   "minute": "0",
 *   "period": "14",
 *   "time": "90+5'",
 *   "type": "end 14",
 *   "teamRef1": "3390h9hdgoxzzofnrpyktanuz",
 *   "teamRef2": "6hmo9mrlz73nwxkshwuu5vsfm"
 * }
 *
 * @param {Object} comment - Comentario del API
 * @returns {Object} Comentario transformado
 */
const transformCommentItem = (comment) => {
  const eventType = getEventType(comment.type);

  return {
    id: comment.id,
    minute: parseInt(comment.minute) || 0,
    period: comment.period || "1",
    time: comment.time || `${comment.minute}'`,
    eventType: eventType,
    comment: comment.comment || "",
    timestamp: comment.timestamp,
  };
};

/**
 * Extrae el tipo de evento del string de tipo del API
 * Busca coincidencias en EVENT_CATALOG usando patrones regex
 * @param {string} type - Tipo de evento del API
 * @returns {string} Tipo de evento normalizado
 */
const getEventType = (type) => {
  if (!type) return "info";

  const typeStr = type.toLowerCase().trim();

  // Patrones de mapeo para tipos específicos del API
  const typeMapping = [
    // Patrones más específicos primero (orden importa)
    { pattern: /\bsecond\s*yellow\s*(card)?\b/, type: "second_yellow_card" },
    { pattern: /\byellow\s+card\b/, type: "yellow_card" },
    { pattern: /\bred\s+card\b/, type: "red_card" },
    { pattern: /\bstart\s+delay\b/, type: "start_delay" },
    { pattern: /\bend\s+delay\b/, type: "end_delay" },
    { pattern: /^end\s+1$/, type: "end_1" },
    { pattern: /^end\s+2$/, type: "end_2" },
    { pattern: /^end\s+14$/, type: "end_14" },
    { pattern: /\badded\s+time\b/, type: "added_time" },
    // FREE KICK - Variantes específicas
    { pattern: /\bfree\s+kick\s+lost\b/, type: "free_kick_lost" },
    { pattern: /\bfree\s+kick\s+won\b/, type: "free_kick_won" },
    { pattern: /\bfree\s+kick\b/, type: "free_kick_won" }, // fallback
    // PENALTY - Variantes específicas
    { pattern: /\bpenalty\s+goal\b/, type: "penalty_goal" },
    { pattern: /\bpenalty\s+won\b/, type: "penalty_won" },
    { pattern: /\bpenalty\s+lost\b/, type: "penalty_lost" },
    { pattern: /\bpenalty\s+saved\b/, type: "penalty_saved" },
    { pattern: /\bpenalty\b/, type: "penalty_won" }, // fallback
    // ATTEMPT - Variantes específicas
    { pattern: /\battempt\s+blocked\b/, type: "attempt_blocked" },
    { pattern: /\battempt\s+saved\b/, type: "attempt_saved" },
    // VAR y CONTENTIOUS DECISIONS
    { pattern: /\bcontentious\s+referee\s+decisions\b/, type: "contentious_referee_decisions" },
    { pattern: /\bvar\s+cancelled\s+goal\b/, type: "var_cancelled_goal" },
    { pattern: /\bvar\b|\bcontentious\b/, type: "contentious_referee_decisions" },
    // Patrones simples
    { pattern: /\bown\s+goal\b/, type: "own_goal" },
    { pattern: /\bgoal\b/, type: "goal" },
    { pattern: /\bsubstitution\b/, type: "substitution" },
    { pattern: /\bcorner\b/, type: "corner" },
    { pattern: /\boffside\b/, type: "offside" },
    { pattern: /\bpost\b/, type: "post" },
    { pattern: /\bmiss\b/, type: "miss" },
    { pattern: /\bstart\b/, type: "start" },
    { pattern: /\bend\b/, type: "end_delay" }, // fallback genérico
    { pattern: /\blineup\b/, type: "lineup" },
  ];

  for (const { pattern, type: eventType } of typeMapping) {
    if (pattern.test(typeStr)) {
      return eventType;
    }
  }

  return "info";
};